/**
 * V3 client replay config (spec F): allowlist masking. Text is masked unless
 * it sits in the product's own chrome (`[data-ph-unmask]`) and outside any
 * `[data-ph-mask]` data container.
 */

import type { SessionRecordingOptions } from 'posthog-js';

import { redactText } from './redact';

interface ClosestCapable {
  closest(selector: string): unknown;
}

function canClosest(el: unknown): el is ClosestCapable {
  return typeof el === 'object' && el !== null && typeof (el as ClosestCapable).closest === 'function';
}

export function maskTextFn(text: string, element?: unknown): string {
  if (canClosest(element) && element.closest('[data-ph-unmask]') && !element.closest('[data-ph-mask]')) {
    return redactText(text, 10_000);
  }
  return text.replace(/\S/g, '•').slice(0, 24);
}

const MASKED_ATTRIBUTES = /^(title|aria-label|aria-description|alt|placeholder|value|data-value)$/;
const URL_ATTRIBUTES = /^(href|src)$/;

export function maskAttributeFn(name: string, value: string): string {
  if (MASKED_ATTRIBUTES.test(name)) return '•••';
  if (URL_ATTRIBUTES.test(name)) return value.split('?')[0] ?? '';
  return value;
}

type NetworkRequest = Parameters<NonNullable<SessionRecordingOptions['maskCapturedNetworkRequestFn']>>[0];

export function maskCapturedNetworkRequest(request: NetworkRequest): NetworkRequest {
  return { ...request, name: (request.name ?? '').split('?')[0] ?? '' };
}

/**
 * `session_recording` of `replay: 'v3'`. No client `sampleRate` (it would
 * override the remote trigger groups) and no `maskAllElementAttributes` (it
 * would win over `maskAttributeFn`).
 */
export function v3SessionRecording(): SessionRecordingOptions {
  return {
    maskAllInputs: true,
    maskTextSelector: '*',
    maskTextFn: (text, element) => maskTextFn(text, element),
    maskAttributeFn: (name, value) => maskAttributeFn(name, value),
    blockSelector: '[data-ph-block]',
    recordHeaders: false,
    recordBody: false,
    maskCapturedNetworkRequestFn: maskCapturedNetworkRequest,
  };
}

/** Autocapture attributes never sent (spec F); `data-attr` survives as the click label. */
export const ELEMENT_ATTRIBUTE_IGNORELIST = [
  'title',
  'aria-label',
  'aria-description',
  'alt',
  'placeholder',
  'value',
  'data-value',
  'href',
];
